<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'
import { Slider } from '@/components/ui/slider'
import DetailCollectedData from './DetailCollectedData.vue'
import DetailPlanetOrder from './DetailPlanetOrder.vue'
import DetailStaticLayer from './DetailStaticLayer.vue'

const route = useRoute()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const opacity = ref(100)
const map = computed(() => store.getters['geo/getMap'])

const layer = computed(() => {
  const l = store.getters['layers/getOrgLayerById'](orgId.value, layerId.value)
  return l
})

const isCollectedDataLayer = computed(() => {
  return layer.value?.properties?.type === 4
})

const isPlanetOrderLayer = computed(() => {
  return layer.value?.properties?.type === 1
})

const isStaticLayer = computed(() => {
  return layer.value?.properties?.type === 5
})

const isLayerWithFeatures = computed(() => {
  return isCollectedDataLayer.value || isStaticLayer.value
})

const layerCode = computed(() => {
  return layer.value?.uuid
})

const settings = computed(() => store.getters['user/getUserSettings'])

const onOpacityChange = (value) => {
  opacity.value = value[0]

  if (!map.value || !layerId.value) return

  try {
    const layers = map.value.getStyle().layers.filter((layer) =>
      layer.metadata?.groupId === layerId.value
    )

    layers.forEach((layer) => {
      if (layer.type === 'fill') {
        map.value.setPaintProperty(
          layer.id,
          'fill-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'line') {
        map.value.setPaintProperty(
          layer.id,
          'line-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'circle') {
        map.value.setPaintProperty(
          layer.id,
          'circle-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'symbol') {
        map.value.setPaintProperty(
          layer.id,
          'text-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'raster') {
        map.value.setPaintProperty(
          layer.id,
          'raster-opacity',
          opacity.value / 100
        )
      }
    })

    const updatedLayer = {
      ...layer.value,
      properties: {
        ...layer.value.properties,
        // Preserve existing features data when updating opacity
        ...(layer.value?.properties?.features ? {
          features: layer.value.properties.features,
          featureCollection: layer.value.properties.featureCollection
        } : {}),
        opacity: opacity.value / 100
      }
    }

    store.dispatch('layers/updateOrgLayer', {
      orgId: orgId.value,
      layer: updatedLayer
    })
  } catch (error) {
    console.error('Error updating layer opacity:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to update layer opacity',
      variant: 'destructive'
    })
  }
}

onMounted(() => {
  if (layer.value?.properties?.opacity !== undefined) {
    opacity.value = Math.round(layer.value.properties.opacity * 100)
  }
})

onUnmounted(() => {
  // Clear dates when leaving the layer details page to prevent data persistence between organizations
  store.commit('layers/CLEAR_DATES')
})
</script>

<template>
  <div class="p-3" v-if="layer">
    <div class="flex justify-between items-center">
      <h3 class="text-lg font-bold">{{ layer.properties.name || 'Unnamed' }}</h3>
    </div>
    <ul class="text-gray-500">
      <li><span>Created at: </span> {{ formatTime(layer?.properties?.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
      <li v-if="layer?.properties?.updated_at !== layer?.properties?.created_at">Updated at: {{ formatTime(layer?.properties?.updated_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
    </ul>

    <div class="mt-4">
      <!-- Collected Data Layer (type 4) -->
      <DetailCollectedData
        v-if="isCollectedDataLayer"
        :layer="layer"
        :activeTab="'data'"
      />

      <!-- Planet Order Layer (type 1) -->
      <DetailPlanetOrder
        v-else-if="isPlanetOrderLayer"
        :layer="layer"
        :activeTab="'data'"
      />

      <!-- Static Layer (type 5) -->
      <DetailStaticLayer
        v-else-if="isStaticLayer"
        :layer="layer"
      />

      <!-- Other layer types -->
      <p v-else class="text-sm text-muted-foreground">No additional data available for this layer</p>
    </div>
  </div>
</template>
